import { Link, useLocation } from 'wouter'
import { cn } from '@/lib/utils'
import {
  LayoutDashboard,
  KeyRound,
  BrainCircuit,
  Database,
  BarChart3,
  BookOpen,
  HardDrive,
  Settings,
  ChevronRight,
  Flame,
  LogOut,
  Activity,
  AlertTriangle,
  Network,
  TerminalSquare,
  Code2,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useSWR from 'swr'

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then(r => r.json())

const NAV_ITEMS = [
  {
    group: 'Overview',
    items: [
      { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { href: '/dashboard/analytics', label: 'Analytics', icon: BarChart3, badge: 'Live' },
      { href: 'https://x0-83.lavaflowstreams.pro', label: 'x0-83 IDE', icon: Code2, badge: 'NEW' },
    ],
  },
  {
    group: 'Connect',
    items: [
      { href: '/portal', label: 'Connection Strings', icon: Network, badge: 'SDK' },
      { href: '/playground', label: 'SQL Console', icon: TerminalSquare },
    ],
  },
  {
    group: 'API',
    items: [
      { href: '/dashboard/keys', label: 'API Keys', icon: KeyRound },
      { href: '/dashboard/gateway', label: 'AI Gateway', icon: BrainCircuit },
      { href: '/dashboard/docs', label: 'Documentation', icon: BookOpen },
    ],
  },
  {
    group: 'Infrastructure',
    items: [
      { href: '/dashboard/database', label: 'EdgeSQL D1', icon: Database },
      { href: '/dashboard/storage', label: 'R2 Storage', icon: HardDrive },
      { href: '/dashboard/connectivity', label: 'Connectivity', icon: Network },
    ],
  },
  {
    group: 'System',
    items: [
      { href: '/dashboard/settings', label: 'Settings', icon: Settings },
    ],
  },
]

interface SidebarProps {
  collapsed?: boolean
}

export function Sidebar({ collapsed = false }: SidebarProps) {
  const [pathname] = useLocation()
  const { data: health } = useSWR('/api/health', fetcher, { refreshInterval: 30_000 })
  const d1Status: 'ok' | 'unauthorized' | 'unreachable' | null = health?.d1?.status ?? null

  async function handleLogout() {
    await fetch('/api/auth/logout', { 
      method: 'POST',
      credentials: 'include'
    })
    window.location.href = '/login'
  }

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'flex flex-col h-full bg-sidebar border-r border-sidebar-border transition-all duration-200',
          collapsed ? 'w-16' : 'w-60'
        )}
      >
        {/* Brand */}
        <div className={cn('flex items-center gap-3 px-4 py-5 border-b border-sidebar-border', collapsed && 'justify-center px-0')}>
          <div className="flex-shrink-0 w-8 h-8 rounded-md lava-gradient flex items-center justify-center shadow-sm">
            <Flame className="w-4 h-4 text-lava-foreground" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-semibold text-sidebar-foreground truncate">LavaFlow</p>
              <p className="text-[10px] text-muted-foreground font-mono truncate">streams.pro</p>
            </div>
          )}
        </div>

        {/* D1 Status indicator */}
        {!collapsed && (
          <div className={cn(
            'flex items-center gap-2 mx-3 my-2 px-3 py-2 rounded-md border',
            d1Status === 'ok' && 'bg-success/10 border-success/20',
            d1Status === 'unauthorized' && 'bg-warning/10 border-warning/20',
            d1Status === 'unreachable' && 'bg-destructive/10 border-destructive/20',
            !d1Status && 'bg-muted/50 border-border',
          )}>
            {d1Status === 'ok' && <Activity className="w-3 h-3 text-success flex-shrink-0 animate-pulse" />}
            {d1Status === 'unauthorized' && <AlertTriangle className="w-3 h-3 text-warning flex-shrink-0" />}
            {(d1Status === 'unreachable' || !d1Status) && <Activity className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
            <span className={cn(
              'text-[11px] font-medium',
              d1Status === 'ok' && 'text-success',
              d1Status === 'unauthorized' && 'text-warning',
              d1Status === 'unreachable' && 'text-destructive',
              !d1Status && 'text-muted-foreground',
            )}>
              {d1Status === 'ok' && 'D1 connected'}
              {d1Status === 'unauthorized' && 'D1 auth error'}
              {d1Status === 'unreachable' && 'D1 unreachable'}
              {!d1Status && 'Checking...'}
            </span>
            {health?.d1?.latency_ms > 0 && d1Status === 'ok' && (
              <span className="text-[10px] text-muted-foreground ml-auto font-mono">{health.d1.latency_ms}ms</span>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-4">
          {NAV_ITEMS.map((group) => (
            <div key={group.group}>
              {!collapsed && (
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 px-2 mb-1">
                  {group.group}
                </p>
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const isActive = pathname === item.href || pathname.startsWith(item.href + '/')
                  const Icon = item.icon

                  const link = (
                    <Link
                      href={item.href}
                      className={cn(
                        'flex items-center gap-3 px-2.5 py-2 rounded-md text-sm transition-colors group',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                          : 'text-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                        collapsed && 'justify-center px-0 w-10 h-10 mx-auto'
                      )}
                    >
                      <Icon
                        className={cn(
                          'flex-shrink-0 transition-colors',
                          collapsed ? 'w-5 h-5' : 'w-4 h-4',
                          isActive ? 'text-lava' : 'text-muted-foreground group-hover:text-foreground'
                        )}
                      />
                      {!collapsed && (
                        <>
                          <span className="flex-1 truncate">{item.label}</span>
                          {'badge' in item && item.badge && (
                            <Badge
                              variant="secondary"
                              className="text-[9px] px-1.5 py-0 h-4 bg-lava/10 text-lava border-lava/20"
                            >
                              {item.badge}
                            </Badge>
                          )}
                          {isActive && (
                            <ChevronRight className="w-3 h-3 text-lava opacity-60 flex-shrink-0" />
                          )}
                        </>
                      )}
                    </Link>
                  )

                  if (collapsed) {
                    return (
                      <li key={item.href}>
                        <Tooltip>
                          <TooltipTrigger asChild>{link}</TooltipTrigger>
                          <TooltipContent side="right" className="text-xs">
                            {item.label}
                          </TooltipContent>
                        </Tooltip>
                      </li>
                    )
                  }

                  return <li key={item.href}>{link}</li>
                })}
              </ul>
            </div>
          ))}
        </nav>

        <Separator className="bg-sidebar-border" />

        {/* Footer */}
        <div className={cn('p-3', collapsed && 'flex justify-center')}>
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-9 h-9 text-muted-foreground hover:text-destructive"
                  onClick={handleLogout}
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Sign Out</TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-2 py-2 rounded-md text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          )}
        </div>
      </aside>
    </TooltipProvider>
  )
}
